import { Component, EventEmitter, Input, Output } from '@angular/core';

import { SecondMs } from 'rev-shared/date/Time.Constant';
import { formatTimespanShort } from 'rev-shared/date/DateFormatters';
import { srtTimeToSeconds } from 'rev-shared/util/SrtUtil';

import { IChatBotMessage, MessageAuthor, MessageNodeType } from './VideoAssistantTypes';
import { VideoAiAssistantService } from './VideoAiAssistant.Service';

import styles from './VideoAiAssistantContainer.module.less';

@Component({
	selector: 'video-ai-assistant-bot-message',
	template: `
		<div [ngClass]="[styles.flexRow, styles.message, styles.botMessage]">
			<vb-profile-picture [placeholderIconClass]="'lightbulb'"size="large"></vb-profile-picture>
			<div [ngClass]="styles.flexColumn">
				<div [ngClass]="styles.header">
					<span>{{ 'UI_AI' | translate }}</span>
					<span> - <i>{{ message.timestamp | vbDateTimeMedium }}</i></span>
				</div>
				@if (message.isErrorMessage) {
					<div [ngClass]="styles.errorContent">{{ 'Media_Videos_Assistant_Error' | translate }}</div>
				} @else {
					<div [ngClass]="styles.messageWrapper">
						@for(node of message.content; track node) {
							@switch(node.type) {
								@case (MessageNodeType.PARAGRAPH) {
									<div [ngClass]="styles.messageContent">
										@for (child of node.children; track child) {
											@if (child.type === 'text') {
												<span [innerHTML]="child.content"></span>
											} @else if (child.type === 'timestamp') {
												<button
													type="button"
													class="btn btn-transparent"
													[ngClass]="styles.timestampBtn"
													(click)="onTimeClick(child.content)"
												>
													{{ ((child.content | vbSrtTimeToSeconds) * SecondMs) | vbTimespan }}
												</button>
											}
										}
									</div>
								}
								@case (MessageNodeType.CITATIONS) {
									<div [ngClass]="styles.citationsContainer">

										<button
											type="button"
											class="btn btn-transparent"
											[ngClass]="styles.toggleCitationsBtn"
											(click)="toggleCitations()"
										>
											<span>{{ 'Media_Videos_Assistant_CitationsHeader' | translate }}</span>
											<span class="vb-icon vb-icon-chevron" [ngClass]="[styles.chevron, !showCitations ? styles.chevronRight : '']"></span>
										</button>

										@if (showCitations) {
											<div [ngClass]="styles.citationsList">
												@for (citation of node.items; track citation) {
													<div [ngClass]="styles.citationItem">
														<button
															type="button"
															class="btn btn-transparent"
															[ngClass]="styles.timestampBtn"
															(click)="onTimeClick(citation.timestamp)"
														>
															{{ ((citation.timestamp | vbSrtTimeToSeconds) * SecondMs) | vbTimespan }}
														</button>
														<span
															[tooltip]="citation.text"
															[placement]="'bottom'"
															[containerClass]="styles.citationTooltip"
														>
															{{ citation.text }}
														</span>
													</div>
												}
											</div>
										}

									</div>
								}
							}
						}
					</div>

					<div [ngClass]="styles.messageActions">
						<button
							type="button"
							class="btn btn-transparent"
							[attr.aria-label]="'Copy' | translate"
							[tooltip]="'Copy' | translate"
							[placement]="'bottom'"
							(click)="onCopyClick()"
						>
							<span class="vb-icon vb-icon-center vb-icon-copy"></span>
						</button>
					</div>
				}
			</div>
		</div>
	`,

})
export class VideoAiAssistantBotMessageComponent {
	@Input() public message: IChatBotMessage;
	@Output() public onTimeClicked: EventEmitter<number> = new EventEmitter<number>();
	@Output() public onCopyClicked: EventEmitter<string> = new EventEmitter<string>();

	public readonly styles = styles;
	public readonly MessageAuthor = MessageAuthor;
	public readonly MessageNodeType = MessageNodeType;
	public readonly SecondMs = SecondMs;

	public messageTextHTML: string = '';
	public showCitations: boolean = false;

	constructor(
		public readonly VideoAiAssistantService: VideoAiAssistantService
	) { }

	public onTimeClick(time: string): void {
		const timeInSeconds = srtTimeToSeconds(time);
		this.onTimeClicked.emit(timeInSeconds);
	}

	public onCopyClick(): void {
		const paragraphs = this.message.content
			.filter(node => node.type === MessageNodeType.PARAGRAPH);

		const lines = paragraphs.map(paragraph => {
			return paragraph.children.map(child => {
				if (child.type === MessageNodeType.TEXT) {
					return child.content;
				}
				if (child.type === MessageNodeType.TIMESTAMP) {
					return `[${formatTimespanShort(srtTimeToSeconds(child.content) * SecondMs)}]`;
				}
				return '';
			}).join('');
		});

		const textToCopy = lines.join('\n\n');
		this.onCopyClicked.emit(textToCopy);
	}

	public toggleCitations(): void {
		this.showCitations = !this.showCitations;
	}

}
